// The Follow-ups screen (client, 2026-10-30): "below sales pipeline one more
// screen for follow-ups only, connected to all follow-ups — sales pipeline,
// quotations, sample, invoice — where what is not added, added and due is all
// there, with all the necessary filters".
//
// Tiles double as tabs: Overdue · Due today · Upcoming · Not added · Done.
// "Not added" is the open work with no next follow-up (server rules:
// src/shared/followups/board.js — the same as the Daily-work monitor).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CalendarCheck, CalendarClock, CalendarPlus, CheckCircle2, CircleSlash, Phone, RefreshCw, Search, X } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard } from '@/shared/components/ui/StatCard';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Modal } from '@/shared/components/ui/Modal';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/shared/utils/utils';
import { CloseFollowupDialog, outcomeLabel, FOLLOWUP_OUTCOMES, type CloseFollowupTarget } from '@/features/activity/components/CloseFollowupDialog';
import { useCreateTimelineRow } from '@/features/activity/hooks/useFollowups';
import { useRememberedState } from '@/shared/hooks/useRememberedFilters';
import { useFollowupBoard, type BoardFollowup, type BoardScope, type FollowupBucket, type MissingItem } from '../api';

type Tab = FollowupBucket | 'missing' | 'all';
/** Tabs that are themselves a date range — a date filter on top of them can contradict. */
const DUE_TABS: Tab[] = ['overdue', 'today', 'upcoming'];

/** What a follow-up is for, grouped the way the team talks about it. */
const TYPE_GROUPS: Array<{ value: string; label: string; types: string[] }> = [
  { value: 'pipeline', label: 'Leads & deals', types: ['lead', 'deal', 'enquiry'] },
  { value: 'quotation', label: 'Quotations', types: ['quotation'] },
  { value: 'proforma', label: 'Proforma invoices', types: ['proforma'] },
  { value: 'sample', label: 'Samples', types: ['sample'] },
  { value: 'invoice', label: 'Invoices', types: ['invoice', 'sales'] },
  { value: 'visit', label: 'Visits', types: ['visit'] },
];
const typeGroupOf = (parentType: string) => TYPE_GROUPS.find((g) => g.types.includes(parentType))?.value || 'other';
const typeLabel = (parentType: string) => TYPE_GROUPS.find((g) => g.types.includes(parentType))?.label.replace(/s$/, '').replace('Leads & deal', 'Lead / deal') || 'Other';

const CATEGORIES = [
  { value: 'order', label: 'Order' },
  { value: 'payment', label: 'Payment' },
  { value: 'visit', label: 'Visit' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'custom', label: 'Custom' },
  { value: 'other', label: 'Other' },
];

const CONTACT_METHODS = [
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

const DAY = 24 * 3600 * 1000;
/** Date and time — or just the date for a follow-up saved without a time (midnight UTC, shown as 5:30 am). */
const fmtDateTime = (d?: string | null) => {
  if (!d) return '—';
  const x = new Date(d);
  const dateOnly = x.getUTCHours() === 0 && x.getUTCMinutes() === 0 && x.getUTCSeconds() === 0;
  return x.toLocaleString('en-IN', dateOnly
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const dayStart = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/** Quick date filters (client, 2026-10-30). Weeks run Monday to Sunday. */
const DATE_PRESETS = [
  { value: '', label: 'Any date' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'custom', label: 'Custom dates' },
] as const;
type DatePreset = typeof DATE_PRESETS[number]['value'];

/** [from, to) in ms for a preset, from local midnight; null for "any" / custom. */
function presetRange(preset: DatePreset, now = new Date()): [number, number] | null {
  const today = dayStart(now).getTime();
  const monday = today - ((new Date(today).getDay() + 6) % 7) * DAY;
  switch (preset) {
    case 'today': return [today, today + DAY];
    case 'yesterday': return [today - DAY, today];
    case 'this_week': return [monday, monday + 7 * DAY];
    case 'last_week': return [monday - 7 * DAY, monday];
    default: return null;
  }
}

/** "3 days overdue" / "in 2 days" / "today 4:30 pm". */
function dueHint(f: BoardFollowup) {
  if (f.bucket === 'done') return f.closingDate ? `Closed ${fmtDate(f.closingDate)}` : 'Closed';
  const due = new Date(f.followupDate);
  const days = Math.round((dayStart(due).getTime() - dayStart(new Date()).getTime()) / DAY);
  if (days < 0) return `${-days} day${days === -1 ? '' : 's'} overdue`;
  const timed = !(due.getUTCHours() === 0 && due.getUTCMinutes() === 0);
  if (days === 0) return timed ? `Today ${due.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}` : 'Today';
  return days === 1 ? 'Tomorrow' : `In ${days} days`;
}

/** Tomorrow 11:00, as a datetime-local value. */
function defaultNext() {
  const d = new Date(Date.now() + DAY);
  d.setHours(11, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const control = 'h-9 rounded-lg border border-input bg-surface px-3 text-body-sm text-on-surface outline-none focus:border-primary';

export function FollowupsPage() {
  // Remembered (per user), so opening a record and coming Back — or coming
  // back tomorrow — shows the same tab and filters.
  const page = 'followups-board';
  const [scope, setScope] = useRememberedState<BoardScope>(page, 'scope', 'mine');
  const [doneDays, setDoneDays] = useRememberedState(page, 'doneDays', 30);
  const board = useFollowupBoard(scope, doneDays);
  const qc = useQueryClient();

  const [tab, setTab] = useRememberedState<Tab>(page, 'tab', 'overdue');
  const [search, setSearch] = useRememberedState(page, 'search', '');
  const [type, setType] = useRememberedState(page, 'type', '');
  const [person, setPerson] = useRememberedState(page, 'person', '');
  const [category, setCategory] = useRememberedState(page, 'category', '');
  const [outcome, setOutcome] = useRememberedState(page, 'outcome', '');
  const [preset, setPreset] = useRememberedState<DatePreset>(page, 'datePreset', '');
  const [from, setFrom] = useRememberedState(page, 'from', '');
  const [to, setTo] = useRememberedState(page, 'to', '');
  const [closing, setClosing] = useState<CloseFollowupTarget | null>(null);
  const [adding, setAdding] = useState<AddTarget | 'pick' | null>(null);

  const data = board.data;
  const effectiveScope = data?.scope ?? scope;

  // Filters shared by both lists.
  const q = search.trim().toLowerCase();
  // Open follow-ups are matched on their due date, done ones on the day they
  // were closed — "Today" on Done is what was done today.
  const range = presetRange(preset);
  const fromT = range ? range[0] : preset === 'custom' && from ? dayStart(new Date(from)).getTime() : null;
  const toT = range ? range[1] : preset === 'custom' && to ? dayStart(new Date(to)).getTime() + DAY : null;

  const filteredFollowups = useMemo(() => (data?.followups ?? []).filter((f) => {
    if (type && typeGroupOf(f.parentType) !== type) return false;
    if (person && String(f.representativeId || '') !== person) return false;
    if (category && (f.followupCategory || 'other') !== category) return false;
    if (outcome && (f.outcome || '') !== outcome) return false;
    const when = new Date(f.bucket === 'done' ? (f.closingDate || f.followupDate) : f.followupDate).getTime();
    if (fromT != null && when < fromT) return false;
    if (toT != null && when >= toT) return false;
    if (q) {
      const s = f.parentSummary || {};
      const hay = [s.company, s.number, s.contactName, s.phone, f.followupNote, f.details, f.customerResponse, f.contactPersonName, f.representativeName].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [data?.followups, type, person, category, outcome, fromT, toT, q]);

  const filteredMissing = useMemo(() => (data?.missing ?? []).filter((m) => {
    if (type && typeGroupOf(m.parentType) !== type) return false;
    if (person && String(m.ownerId || '') !== person) return false;
    if (q && ![m.title, m.sub, m.ownerName].join(' ').toLowerCase().includes(q)) return false;
    return true;
  }), [data?.missing, type, person, q]);

  const counts = useMemo(() => {
    const c = { overdue: 0, today: 0, upcoming: 0, done: 0 };
    for (const f of filteredFollowups) c[f.bucket] += 1;
    return c;
  }, [filteredFollowups]);

  const rows = tab === 'missing' ? [] : filteredFollowups
    .filter((f) => tab === 'all' || f.bucket === tab)
    .sort((a, b) => (tab === 'done'
      ? new Date(b.closingDate || b.followupDate).getTime() - new Date(a.closingDate || a.followupDate).getTime()
      : new Date(a.followupDate).getTime() - new Date(b.followupDate).getTime()));

  // A remembered "Overdue" tab with a remembered "Due: Today" filter showed an
  // empty list on every visit (client, 2026-10-30) — fixed once on open.
  useEffect(() => {
    if (preset && DUE_TABS.includes(tab)) setTab('all');
    // Only on open: the date select itself handles later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtersOn = Boolean(search || type || person || category || outcome || preset);
  const clearFilters = () => { setSearch(''); setType(''); setPerson(''); setCategory(''); setOutcome(''); setPreset(''); setFrom(''); setTo(''); };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Follow-ups"
        icon={CalendarCheck}
        description="Every follow-up across leads, quotations, proformas, samples and invoices — what is due, what is done, and what has none."
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            {data?.canWiden && (
              <select aria-label="Whose follow-ups" value={scope} onChange={(e) => setScope(e.target.value as BoardScope)} className={control}>
                <option value="mine">Showing: my follow-ups</option>
                <option value="team">Showing: my team</option>
                <option value="all">Showing: whole company</option>
              </select>
            )}
            <Button onClick={() => setAdding('pick')}>
              <CalendarPlus className="mr-1.5 h-4 w-4" /> Add follow-up
            </Button>
            <Button variant="outline" onClick={() => void board.refetch()} disabled={board.isFetching}>
              <RefreshCw className={cn('mr-1.5 h-4 w-4', board.isFetching && 'animate-spin')} /> Refresh
            </Button>
          </div>
        )}
      />
      {data?.canWiden && effectiveScope !== scope && (
        <p className="text-body-sm text-on-surface-variant">Showing {effectiveScope === 'team' ? 'your team' : 'your own'} follow-ups — that is as wide as your access goes.</p>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Overdue" value={counts.overdue} tone="danger" icon={AlertTriangle} hint="Past their date, still open" onClick={() => setTab('overdue')} active={tab === 'overdue'} loading={board.isLoading} />
        <StatCard label="Due today" value={counts.today} tone="warning" icon={CalendarClock} hint="Planned for today" onClick={() => setTab('today')} active={tab === 'today'} loading={board.isLoading} />
        <StatCard label="Upcoming" value={counts.upcoming} tone="info" icon={CalendarCheck} hint="Scheduled ahead" onClick={() => setTab('upcoming')} active={tab === 'upcoming'} loading={board.isLoading} />
        <StatCard label="Not added" value={filteredMissing.length} tone="danger" icon={CircleSlash} hint="Open work with no next follow-up" onClick={() => setTab('missing')} active={tab === 'missing'} loading={board.isLoading} />
        <StatCard label="Done" value={counts.done} tone="success" icon={CheckCircle2} hint={`Closed in the last ${doneDays} days`} onClick={() => setTab('done')} active={tab === 'done'} loading={board.isLoading} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-outline bg-surface p-3">
        <label className="relative min-w-[14rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, number, phone, note…" aria-label="Search"
            className={cn(control, 'w-full pl-8')} />
        </label>
        <select aria-label="Type" value={type} onChange={(e) => setType(e.target.value)} className={control}>
          <option value="">All types</option>
          {TYPE_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          <option value="other">Other</option>
        </select>
        {(data?.employees.length ?? 0) > 1 && (
          <select aria-label="Salesperson" value={person} onChange={(e) => setPerson(e.target.value)} className={control}>
            <option value="">All salespeople</option>
            {data!.employees.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
          </select>
        )}
        {tab !== 'missing' && (
          <>
            <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} className={control}>
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            {(tab === 'done' || tab === 'all') && (
              <select aria-label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} className={control}>
                <option value="">All outcomes</option>
                {FOLLOWUP_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <select aria-label="Date" value={preset} onChange={(e) => { const v = e.target.value as DatePreset; setPreset(v); if (v && DUE_TABS.includes(tab)) setTab('all'); }} className={control}
              title={tab === 'done' ? 'The day it was closed' : 'The day it is due'}>
              {DATE_PRESETS.map((d) => <option key={d.value} value={d.value}>{d.value ? `${tab === 'done' ? 'Closed' : 'Due'}: ${d.label}` : d.label}</option>)}
            </select>
            {preset === 'custom' && (
              <>
                <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
                  From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={control} aria-label="From date" />
                </label>
                <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
                  to <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={control} aria-label="To date" />
                </label>
              </>
            )}
          </>
        )}
        {tab === 'done' && (
          <select aria-label="Closed within" value={doneDays} onChange={(e) => setDoneDays(Number(e.target.value))} className={control}>
            <option value={7}>Closed in last 7 days</option>
            <option value={30}>Closed in last 30 days</option>
            <option value={90}>Closed in last 90 days</option>
            <option value={365}>Closed in last year</option>
          </select>
        )}
        <button type="button" onClick={() => setTab('all')}
          className={cn('h-9 rounded-lg border px-3 text-body-sm font-medium', tab === 'all' ? 'border-primary bg-primary/10 text-primary' : 'border-input text-on-surface-variant hover:bg-surface-container-low')}>
          All follow-ups
        </button>
        {filtersOn && (
          <Button variant="ghost" onClick={clearFilters}><X className="mr-1 h-4 w-4" /> Clear filters</Button>
        )}
      </div>

      {board.isError ? (
        <EmptyState title="Could not load follow-ups" description={getApiErrorMessage(board.error, 'Try again in a moment.')} action={<Button onClick={() => void board.refetch()}>Try again</Button>} />
      ) : tab === 'missing' ? (
        <MissingTable items={filteredMissing} loading={board.isLoading} showOwner={(data?.employees.length ?? 0) > 1} onAdd={setAdding} />
      ) : (
        <FollowupTable rows={rows} loading={board.isLoading} showOwner={(data?.employees.length ?? 0) > 1} tab={tab}
          elsewhere={([['overdue', 'Overdue'], ['today', 'Due today'], ['upcoming', 'Upcoming'], ['done', 'Done']] as const)
            .filter(([k]) => k !== tab && counts[k] > 0).map(([k, label]) => ({ label: `${label} (${counts[k]})`, onClick: () => setTab(k) }))}
          onClearFilters={filtersOn ? clearFilters : undefined}
          onClose={(f) => setClosing({ id: f._id, parentType: f.parentType, parentId: String(f.parentId), title: f.parentSummary?.company || f.parentSummary?.number, note: f.followupNote })} />
      )}

      <CloseFollowupDialog target={closing} onClose={() => setClosing(null)} onClosed={() => void qc.invalidateQueries({ queryKey: ['followups', 'board'] })} />
      <AddFollowupModal item={adding} scope={effectiveScope} onClose={() => setAdding(null)} />
    </div>
  );
}

const th = 'px-3 py-2 text-left text-label-sm font-semibold uppercase tracking-wide text-on-surface-variant';
const td = 'px-3 py-2.5 align-top text-body-sm text-on-surface';

function TableShell({ children, loading, empty }: { children: React.ReactNode; loading: boolean; empty: React.ReactNode | null }) {
  if (loading) return <div className="rounded-xl border border-outline bg-surface p-8 text-center text-body-sm text-on-surface-variant">Loading follow-ups…</div>;
  if (empty) return <>{empty}</>;
  return <div className="overflow-x-auto rounded-xl border border-outline bg-surface"><table className="w-full min-w-[64rem] table-fixed border-collapse">{children}</table></div>;
}

function ParentCell({ link, title, sub }: { link: string | null; title: string; sub?: string }) {
  return (
    <div className="min-w-0">
      {link ? <Link to={link} className="font-semibold text-primary hover:underline">{title}</Link> : <span className="font-semibold">{title}</span>}
      {sub && <div className="text-body-xs text-on-surface-variant">{sub}</div>}
    </div>
  );
}

function FollowupTable({ rows, loading, showOwner, tab, onClose, elsewhere = [], onClearFilters }: {
  rows: BoardFollowup[]; loading: boolean; showOwner: boolean; tab: Tab; onClose: (f: BoardFollowup) => void;
  /** Other tabs that do have follow-ups, offered when this one is empty. */
  elsewhere?: Array<{ label: string; onClick: () => void }>;
  onClearFilters?: () => void;
}) {
  const emptyText: Record<string, string> = {
    overdue: 'Nothing overdue — every open follow-up is on time.',
    today: 'Nothing planned for today.',
    upcoming: 'Nothing scheduled ahead.',
    done: 'No follow-ups closed in this window.',
    all: 'No follow-ups match these filters.',
  };
  return (
    <TableShell loading={loading} empty={rows.length ? null : (
      <EmptyState
        icon={<CalendarCheck className="h-8 w-8" />}
        title={emptyText[tab] || 'No follow-ups.'}
        description={elsewhere.length ? 'There are follow-ups in:' : onClearFilters ? 'Your filters may be hiding some — clear them to see everything.' : 'Change the filters above to see more.'}
        action={(elsewhere.length || onClearFilters) ? (
          <div className="flex flex-wrap justify-center gap-2">
            {elsewhere.map((e) => <Button key={e.label} size="sm" onClick={e.onClick}>{e.label}</Button>)}
            {onClearFilters && <Button size="sm" variant="outline" onClick={onClearFilters}><X className="mr-1 h-4 w-4" /> Clear filters</Button>}
          </div>
        ) : undefined}
      />
    )}>
      {/* Fixed widths; the follow-up note takes what is left. */}
      <colgroup>
        <col className="w-[9.5rem]" />
        <col className="w-[13rem]" />
        <col />
        <col className="w-[8rem]" />
        {showOwner && <col className="w-[8.5rem]" />}
        <col className="w-[11.5rem]" />
        <col className="w-[8.5rem]" />
        <col className="w-[5.5rem]" />
      </colgroup>
      <thead className="border-b border-outline bg-surface-container-low">
        <tr>
          <th className={th}>Due</th>
          <th className={th}>Client / document</th>
          <th className={th}>Follow-up</th>
          <th className={th}>Type</th>
          {showOwner && <th className={th}>Assigned to</th>}
          <th className={th}>Contact</th>
          <th className={th}>Status</th>
          <th className={cn(th, 'text-right')}>Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f) => {
          const s = f.parentSummary || {};
          const phone = f.contactPersonPhone || s.phone;
          return (
            <tr key={f._id} className="border-b border-outline last:border-0 hover:bg-surface-container-low">
              <td className={td}>
                <div>{fmtDateTime(f.followupDate)}</div>
                <div className={cn('text-body-xs font-medium', f.bucket === 'overdue' ? 'text-danger-text' : f.bucket === 'today' ? 'text-warning-text' : 'text-on-surface-variant')}>{dueHint(f)}</div>
              </td>
              <td className={td}>
                <ParentCell link={f.link} title={s.company || s.contactName || 'Client'} sub={[s.label, s.number].filter(Boolean).join(' ')} />
              </td>
              <td className={cn(td, 'break-words')}>
                <div className="line-clamp-2">{f.followupNote}</div>
                {f.customerResponse && f.bucket === 'done' && <div className="mt-0.5 line-clamp-2 text-body-xs italic text-on-surface-variant">“{f.customerResponse}”</div>}
                {f.noNextReason && <div className="mt-0.5 text-body-xs text-on-surface-variant"><span className="font-medium">No next follow-up:</span> {f.noNextReason}</div>}
              </td>
              <td className={td}><Badge tone="neutral">{typeLabel(f.parentType)}</Badge></td>
              {showOwner && <td className={td}>{f.representativeName || '—'}</td>}
              <td className={cn(td, 'break-words')}>
                <div>{f.contactPersonName || s.contactName || '—'}</div>
                {phone && <a href={`tel:${phone}`} className="inline-flex items-center gap-1 text-body-xs text-primary hover:underline"><Phone className="h-3 w-3" />{phone}</a>}
              </td>
              <td className={td}>
                {f.bucket === 'done'
                  ? <Badge tone="success">{outcomeLabel(f.outcome) || (f.status === 'closed' ? 'Closed' : 'Done')}</Badge>
                  : <Badge tone={f.bucket === 'overdue' ? 'danger' : f.bucket === 'today' ? 'warning' : 'info'}>{f.bucket === 'overdue' ? 'Overdue' : f.bucket === 'today' ? 'Due today' : 'Upcoming'}</Badge>}
              </td>
              <td className={cn(td, 'whitespace-nowrap text-right')}>
                {f.status === 'open'
                  ? <Button size="sm" onClick={() => onClose(f)}>Close</Button>
                  : f.link && <Link to={f.link} className="text-body-sm font-medium text-primary hover:underline">Open</Link>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </TableShell>
  );
}

const KIND_LABEL: Record<MissingItem['kind'], string> = { record: 'Lead / deal', quotation: 'Quotation', proforma: 'Proforma invoice', sample: 'Sample' };

function MissingTable({ items, loading, showOwner, onAdd }: { items: MissingItem[]; loading: boolean; showOwner: boolean; onAdd: (m: AddTarget) => void }) {
  return (
    <TableShell loading={loading} empty={items.length ? null : <EmptyState icon={<CheckCircle2 className="h-8 w-8" />} title="Every open lead, quotation and sample has a next follow-up." description="Nothing is waiting for one." />}>
      <colgroup>
        <col />
        <col className="w-[10rem]" />
        <col className="w-[11rem]" />
        {showOwner && <col className="w-[10rem]" />}
        <col className="w-[10rem]" />
      </colgroup>
      <thead className="border-b border-outline bg-surface-container-low">
        <tr>
          <th className={th}>Client</th>
          <th className={th}>What</th>
          <th className={th}>Waiting since</th>
          {showOwner && <th className={th}>Owner</th>}
          <th className={cn(th, 'text-right')}>Action</th>
        </tr>
      </thead>
      <tbody>
        {items.map((m) => (
          <tr key={`${m.kind}:${m.id}`} className="border-b border-outline last:border-0 hover:bg-surface-container-low">
            <td className={td}><ParentCell link={m.link} title={m.title} sub={m.sub} /></td>
            <td className={td}><Badge tone="neutral">{KIND_LABEL[m.kind]}</Badge></td>
            <td className={cn(td, 'whitespace-nowrap')}>
              <div>{fmtDate(m.date)}</div>
              {m.ageDays != null && <div className="text-body-xs font-medium text-danger-text">{m.ageDays} day{m.ageDays === 1 ? '' : 's'} without one</div>}
            </td>
            {showOwner && <td className={td}>{m.ownerName || '—'}</td>}
            <td className={cn(td, 'whitespace-nowrap text-right')}>
              <Button size="sm" onClick={() => onAdd({ parentType: m.parentType, id: m.id, title: m.title, sub: [KIND_LABEL[m.kind], m.sub].filter(Boolean).join(' · ') })}><CalendarPlus className="mr-1 h-4 w-4" /> Add follow-up</Button>
            </td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}

/** A record a follow-up can be added to — from "Not added", or found by search. */
interface AddTarget { parentType: string; id: string; title: string; sub?: string }

const categoryFor = (parentType: string) => (parentType === 'sample' ? 'feedback' : parentType === 'invoice' ? 'payment' : 'order');

/**
 * Add a follow-up: to the given record ("Not added" rows), or — opened from
 * the header with `item === 'pick'` — to any lead / deal, quotation,
 * proforma, sample or invoice found by name, number or phone (client,
 * 2026-10-30: "yahi se users follow-up bhi add kar sake").
 */
function AddFollowupModal({ item, scope, onClose }: { item: AddTarget | 'pick' | null; scope: BoardScope; onClose: () => void }) {
  const create = useCreateTimelineRow('followups');
  const qc = useQueryClient();
  const [picked, setPicked] = useState<AddTarget | null>(null);
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query.trim(), 300);
  const [when, setWhen] = useState(defaultNext);
  const [method, setMethod] = useState('call');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [openedFor, setOpenedFor] = useState<string | null>(null);

  // Fresh form each time it opens.
  const openKey = item === null ? null : item === 'pick' ? 'pick' : item.id;
  if (openKey !== openedFor) {
    setOpenedFor(openKey); setPicked(null); setQuery(''); setWhen(defaultNext()); setMethod('call'); setNote(''); setError('');
  }
  const target = item && item !== 'pick' ? item : picked;

  const results = useQuery({
    queryKey: ['followups', 'parents', scope, debounced],
    enabled: item === 'pick' && !picked && debounced.length >= 2,
    queryFn: async () => (await api.get<{ items: AddTarget[] }>('/api/followups/parents', { params: { q: debounced, scope } })).data.items,
  });

  const save = () => {
    if (!target) return setError('Find and choose the client or document first.');
    if (!when) return setError('Pick the date of the follow-up.');
    if (new Date(when).getTime() < Date.now() - 60_000) return setError('The follow-up must be in the future.');
    if (!note.trim()) return setError('Write what the follow-up is for.');
    setError('');
    create.mutate({
      parentType: target.parentType,
      parentId: target.id,
      followupDate: new Date(when).toISOString(),
      followupNote: note.trim(),
      contactMethod: method,
      followupCategory: categoryFor(target.parentType),
      status: 'open',
    }, {
      onSuccess: () => {
        toast.success(`Follow-up added for ${target.title}.`);
        void qc.invalidateQueries({ queryKey: ['followups', 'board'] });
        onClose();
      },
      onError: (err) => setError(getApiErrorMessage(err, 'Could not add the follow-up.')),
    });
  };

  return (
    <Modal open={item !== null} onClose={onClose} size="md" title={`Add follow-up${target ? ` — ${target.title}` : ''}`} footer={(
      <>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={create.isPending} disabled={!target}>Add follow-up</Button>
      </>
    )}>
      <div className="space-y-3">
        {item === 'pick' && !picked ? (
          <div className="space-y-2">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Find client or document"
                placeholder="Client name, quotation / invoice / sample number, phone…" className={cn(control, 'w-full pl-8')} />
            </label>
            {debounced.length < 2 ? (
              <p className="text-body-sm text-on-surface-variant">Type at least 2 letters to find an open lead, quotation, proforma, sample or invoice.</p>
            ) : results.isLoading ? (
              <p className="text-body-sm text-on-surface-variant">Searching…</p>
            ) : results.isError ? (
              <p className="text-body-sm text-danger-text">{getApiErrorMessage(results.error, 'Could not search.')}</p>
            ) : (results.data ?? []).length === 0 ? (
              <p className="text-body-sm text-on-surface-variant">Nothing open matches “{debounced}”.</p>
            ) : (
              <ul className="max-h-72 divide-y divide-outline overflow-y-auto rounded-lg border border-outline">
                {(results.data ?? []).map((r) => (
                  <li key={`${r.parentType}:${r.id}`}>
                    <button type="button" onClick={() => { setPicked(r); setError(''); }}
                      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-surface-container-low">
                      <span className="text-body-sm font-semibold text-on-surface">{r.title}</span>
                      {r.sub && <span className="text-body-xs text-on-surface-variant">{r.sub}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : target && (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-surface-container-low px-3 py-2 text-body-sm text-on-surface-variant">
            <span>{target.sub || typeLabel(target.parentType)}</span>
            {item === 'pick' && <button type="button" className="font-medium text-primary hover:underline" onClick={() => setPicked(null)}>Change</button>}
          </div>
        )}
        {target && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium text-on-surface">When <span className="text-danger-text">*</span></span>
                <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={control} />
              </label>
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium text-on-surface">How</span>
                <select value={method} onChange={(e) => setMethod(e.target.value)} className={control}>
                  {CONTACT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-1 text-body-sm">
              <span className="font-medium text-on-surface">What is it for? <span className="text-danger-text">*</span></span>
              <input value={note} onChange={(e) => { setNote(e.target.value); setError(''); }}
                placeholder={target.parentType === 'sample' ? 'e.g. Ask how the trial went' : target.parentType === 'invoice' ? 'e.g. Remind about the payment' : 'e.g. Confirm the order on the quotation'} className={control} />
            </label>
          </>
        )}
        {error && <p className="text-body-sm font-medium text-danger-text">{error}</p>}
      </div>
    </Modal>
  );
}

export default FollowupsPage;
